import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { messagesSelectors, selectMessages } from 'store/messages'
import { getTranslation } from 'utils/translations'
import { useGetDefaultQuestions } from 'api/default-questions'
import { DefaultQuestion } from 'types/default-question'
import { ChatRoleEnum } from 'types/chat'
import { useSendBotQuestion } from 'hooks/useSendBotQuestion'
import { MessageSource } from 'services/analytics'

const USER_ROLES = [ChatRoleEnum.USER, ChatRoleEnum.GUEST]

export const getDefaultQuestionTexts = (defaultQuestions: DefaultQuestion[], language: string): string[] => {
  const texts = (defaultQuestions ?? []).map((question: DefaultQuestion) =>
    getTranslation(question.questionObject, language),
  )
  return [...new Set(texts.filter((text): text is string => !!text))]
}

export const useSuggestedQuestions = () => {
  const { i18n } = useTranslation()
  const messages = useSelector(selectMessages)
  const chatId = useSelector(messagesSelectors.chatId)
  const currentChat = useSelector(messagesSelectors.currentChat)
  const isInOperatorMode = useSelector(messagesSelectors.currentChatIsInOperatorMode)
  const disableFields = useSelector(messagesSelectors.disableFieldsTicketClosed)
  const { defaultQuestions } = useGetDefaultQuestions(currentChat?.areaIds)
  const { sendQuestion, sending, botTyping } = useSendBotQuestion()

  const staticQuestions = useMemo(
    () => getDefaultQuestionTexts(defaultQuestions, i18n.language),
    [defaultQuestions, i18n.language],
  )

  const dynamicQuestions = useSelector(messagesSelectors.currentDynamicQuestions)

  const hasUserMessages = useMemo(
    () => messages.some((message) => USER_ROLES.includes(message.chatSenderRole)),
    [messages],
  )

  const questions = hasUserMessages ? dynamicQuestions : staticQuestions

  // Same chips, two origins: the tenant's static set opens the chat, Mimir generates the rest.
  const questionSource: MessageSource = hasUserMessages ? 'dynamic_question' : 'default_question'
  const sendSuggestedQuestion = useCallback(
    (question: string) => sendQuestion(question, { source: questionSource }),
    [sendQuestion, questionSource],
  )

  const generating = useSelector(messagesSelectors.dynamicQuestionsGenerating)
  const loading = generating && !isInOperatorMode && !disableFields && questions.length === 0

  const visible = !!chatId && !isInOperatorMode && !disableFields && questions.length > 0
  const disabled = sending || botTyping

  return { questions, visible, loading, disabled, sendQuestion: sendSuggestedQuestion }
}
